/**
 * Card-line → evidence-URL and → decision resolution for the HELM page
 * (A7Y-3870).
 *
 * `cards.cjs` tags every rendered line with the keys of the facts items it
 * summarizes (`HelmCardLine.keys`); `buildHelmLinkIndex` resolves the FIRST
 * of those keys that has a durable evidence URL, so every line on the page —
 * including the cap-exempt Slack priority lines — links back to its source
 * thread, ticket or roster row (plan §8, D6). `buildHelmDecisionIndex` reads
 * the same traversal for each item's current `decision`.
 *
 * Which decided items the generic sections (the card itself, Shipped,
 * Judgment queue, Overdue/Stale, Changes) hide is now `overview.suppressedKeys`
 * — computed server-side by `helmSuppressedKeys`/`isHelmItemSuppressed` in
 * `helm/actions.ts` and carried on `HelmOverviewResponse` precisely because
 * that function itself is not reachable through a7y-admin's
 * `@a7y/platform-health/helm` alias (resolves to the import-free
 * `contract.ts` only, plan D1). Sections read it directly rather than
 * through a local predicate here, so there is exactly one rule for "is this
 * suppressed", computed once, on the server.
 */
import { helmSlackPermalink } from '@a7y/platform-health/helm';
import type {
  HelmDecision,
  HelmFacts,
  HelmInboxItem,
  HelmJudgmentQueueItem,
} from '@a7y/platform-health/helm';

interface KeyedFactItem {
  key: string;
  url: string | null;
  decision: HelmDecision | null;
}

/** Every item in a facts snapshot that carries a `key`, either through the
 * common `HelmItemBase` shape (`evidence.ref.url` + `decision` directly) or,
 * for judgment-queue items, their own `url`/`decision` fields — one
 * traversal, reused by both indices below so they can never disagree on
 * which items exist. */
function keyedFactItems(facts: HelmFacts): KeyedFactItem[] {
  const base: {
    key: string;
    evidence: { ref: { url: string | null } };
    decision: HelmDecision | null;
  }[] = [
    ...facts.meetings,
    ...facts.inbox,
    ...facts.sent,
    ...facts.linkedin_mail,
    ...facts.tickets,
    ...facts.deployed,
    ...facts.commitments,
    ...facts.git_since,
    ...facts.drive_notes,
    ...facts.new_senders,
    ...facts.captures,
    ...facts.proposals,
  ];
  const judgmentQueue: HelmJudgmentQueueItem[] = facts.judgment_queue.items;
  return [
    ...base.map((item) => ({ key: item.key, url: item.evidence.ref.url, decision: item.decision })),
    ...judgmentQueue.map((item) => ({ key: item.key, url: item.url, decision: item.decision })),
  ];
}

/** A Slack item's permalink from its own key (`slack:<channel>:<ts>`), for
 * snapshots collected before the collector recorded one. */
function slackPermalinkForKey(key: string): string | null {
  const [prefix, channelId, ts] = key.split(':');
  return prefix === 'slack' && channelId && ts ? helmSlackPermalink(channelId, ts) : null;
}

/** `key → first-seen URL` over one facts snapshot. Built once per render and
 * passed to every section so this file's traversal happens a single time. */
export function buildHelmLinkIndex(facts: HelmFacts): Map<string, string | null> {
  const index = new Map<string, string | null>();
  for (const { key, url } of keyedFactItems(facts)) {
    if (!index.has(key)) index.set(key, url ?? slackPermalinkForKey(key));
  }
  return index;
}

/** `key → current decision` over one facts snapshot. */
export function buildHelmDecisionIndex(facts: HelmFacts): Map<string, HelmDecision | null> {
  const index = new Map<string, HelmDecision | null>();
  for (const { key, decision } of keyedFactItems(facts)) {
    if (!index.has(key)) index.set(key, decision);
  }
  return index;
}

function truncate(text: string | null | undefined, max = 60): string | null {
  if (!text) return null;
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/**
 * Gmail/Slack sender disclosure — mirrors `describeItem`'s sender branch in
 * `scripts/helm/cards.cjs` verbatim (that file is out of a7y-admin's reach;
 * this is the same rule reimplemented, not a different one).
 * A Slack sender with no resolved `users.info` name has `address`
 * as the raw Slack user id (`U…`) — that must never reach the page bare, so
 * it is disclosed as `name unavailable (U…)`, exactly what the card itself
 * prints for the identical case. A Gmail sender's `address` is an email, not
 * an internal id, so it is shown as-is when there is no display name.
 */
function inboxSenderName(item: HelmInboxItem): string {
  const address = item.sender.address;
  const who =
    item.sender.name ||
    (address && address.startsWith('U') ? `name unavailable (${address})` : address) ||
    'unknown';
  const what = item.subject || item.snippet || '';
  return what ? `${who} — ${what}` : who;
}

/**
 * `key → a human-readable name for the item` — so `Changes` never renders only `changes_since_last`'s generic `label` ("newly
 * critical" on every row, with no way to tell which item). It is the fallback
 * for a change entry with no stored `name` (A7Y-5873) that `helmCriticalRowName`
 * could not resolve: it reads whichever facts array actually holds the item,
 * using the same per-kind field each item's
 * own section already uses to identify it (a ticket's `identifier`, a
 * meeting's `title`, a commitment's `name`, an inbox item's sender/subject,
 * and so on).
 */
export function buildHelmDisplayNameIndex(facts: HelmFacts): Map<string, string> {
  const index = new Map<string, string>();
  const set = (key: string, name: string | null | undefined) => {
    if (name && !index.has(key)) index.set(key, name);
  };

  for (const m of facts.meetings) set(m.key, m.title);
  for (const i of facts.inbox) set(i.key, inboxSenderName(i));
  for (const s of facts.sent) set(s.key, s.subject || s.to[0]);
  for (const l of facts.linkedin_mail) set(l.key, l.headline || l.sender);
  for (const t of facts.tickets) set(t.key, t.identifier);
  for (const d of facts.deployed) set(d.key, d.identifier);
  for (const c of facts.commitments) set(c.key, c.name);
  for (const g of facts.git_since) set(g.key, truncate(g.subject));
  for (const n of facts.drive_notes) set(n.key, n.name);
  for (const s of facts.new_senders) set(s.key, s.name || s.address);
  for (const c of facts.captures) set(c.key, truncate(c.note));
  for (const j of facts.judgment_queue.items) set(j.key, j.identifier);

  return index;
}

const FALLBACK_KIND_LABELS: Record<string, string> = {
  gmail: 'Gmail',
  slack: 'a Slack message',
  cal: 'a meeting',
  git: 'a commit',
  drive: 'a Drive note',
  capture: 'a captured note',
};

/**
 * Best-effort, honestly-disclosed label for a key whose item is not in the
 * current facts snapshot — the common case for `changes_since_last.resolved`
 * (a resolved item is usually already gone from the
 * facts a7y-admin has, so `buildHelmDisplayNameIndex` has nothing to look
 * up). Never leaks an opaque internal id — a Gmail message id, a Slack
 * timestamp, a Drive file id: those fall back to a generic, honest per-source
 * description. A Linear/judgment-queue/deployed key's own identifier segment
 * IS the human-facing ticket id already shown elsewhere on the page (e.g.
 * "A7Y-9001"), so that one is used directly. A roster key's slug is not a
 * title (A7Y-5873), so a roster row says its title was not stored.
 */
export function fallbackChangeName(key: string): string {
  const segments = key.split(':');
  const prefix = segments[0];
  const second = segments[1];
  if (prefix === 'linear' || prefix === 'jq' || prefix === 'deployed') return second || 'a ticket';
  // A7Y-5873: a key's slug is not a title ("Pickem Enric S Answer On The 500 Month …"
  // on Trends, 26 Oct): a row whose title the run did not store says so.
  if (prefix === 'roster')
    return `a ${(second ?? 'roster').replace(/_/g, ' ')} row (name unavailable: its title was not stored with this change)`;
  if (prefix === 'sender') return second || 'a new sender';
  return FALLBACK_KIND_LABELS[prefix] ?? 'an item';
}

/** The first key in `keys` that resolves to a non-null URL in `index`, or
 * `null` when none do (an unknown or dropped key — `cards.cjs` already counts
 * those in `droppedUnknownKeyLines`, so the page rendering plain text here is
 * the correct behaviour, not a bug to route around). */
export function firstLinkForKeys(
  index: Map<string, string | null>,
  keys: readonly string[]
): string | null {
  for (const key of keys) {
    const url = index.get(key);
    if (url) return url;
  }
  return null;
}
